import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import WaitingListTableWrapper from '../WaitingListTableWrapper';
import { DataRoomShareableLinkRequest } from 'models/ShareableLink';
import { Button } from 'ncoded-component-library';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import useCallbackAsync from 'hooks/useCallbackAsync';
import XIcon from 'icons/X.icon';
import RevertIcon from 'icons/Revert.icon';
import CreateNewSecureLinkButton from '../CreateNewSecureLinkButton';
import ResolvePopup from 'components/ResolvePopup';

import './WaitingListExpiredTable.styles.scss';
import './WaitingListExpiredTable.styles.responsive.scss';

type WaitingListExpiredTableProps = {
  className?: string;
};

const WaitingListExpiredTable: React.FC<WaitingListExpiredTableProps> = (
  props,
) => {
  const { className } = props;

  const classes = classNames('waiting-list-expired-table', className);

  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { waitingRoomItemsExpired, removeWaitingRoomSecureLinkExpired } =
    useContext(MembersContext);

  const { popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const [onRenew, isRenewing] = useCallbackAsync(
    async (item: DataRoomShareableLinkRequest) => {
      await dataRoomApi.shareableLinks.changeShareableLinkState(
        item.dataRoomShareableLinkId,
        true,
      );

      popSuccess(t('secureLinkSuccesfullyRenew'));
      removeWaitingRoomSecureLinkExpired(item.dataRoomShareableLinkId);
    },
  );

  const [onIgnore, isIgnoring] = useCallbackAsync(
    async (item: DataRoomShareableLinkRequest) => {
      await dataRoomApi.shareableLinks.ignore(item._id);

      popSuccess(t('secureLinkSuccesfullyIgnore'));
      removeWaitingRoomSecureLinkExpired(item.dataRoomShareableLinkId);
    },
  );

  const renderButtons = useCallback(
    (item: DataRoomShareableLinkRequest) => (
      <>
        <ResolvePopup onHover popupContent={t('close')}>
          <Button
            variant="icon"
            className="alert"
            icon={XIcon}
            disabled={doesntHaveAccess || isIgnoring}
            onClick={() => {
              if (doesntHaveAccess) return;
              onIgnore(item);
            }}
          />
        </ResolvePopup>

        <CreateNewSecureLinkButton
          dataRoomShareableLink={item.dataRoomShareableLink}
          dataRoomShareableLinkRequestId={item._id}
          email={item.dataRoomShareableLinkConsumer.email}
        />

        <ResolvePopup onHover popupContent={t('renew')}>
          <Button
            variant="icon"
            className="success"
            icon={RevertIcon}
            disabled={doesntHaveAccess || isRenewing}
            onClick={async () => {
              if (doesntHaveAccess) return;
              onRenew(item);
            }}
          />
        </ResolvePopup>
      </>
    ),
    [doesntHaveAccess, isIgnoring, isRenewing, onIgnore, onRenew, t],
  );

  return (
    <WaitingListTableWrapper
      className={classes}
      items={waitingRoomItemsExpired}
      getActions={renderButtons}
    />
  );
};

export default WaitingListExpiredTable;
